import Button from '@/components/Buttons/Button';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { upgradeFreePlanModalOpen } from '@/components/Modals/UpgradeFreePlanModal';
import { SimpleVideoPlayerModal } from '@/components/SimpleVideoPlayerModal';
import { persistentClipsDns } from '@/environment';
import { useFreeVideos } from '@/hooks/useFreeVideos';
import PlayPreview from '@/images/PlayPreview.svg';
import flags from '@/pages/translation/images/Flags.svg';
import { createTranslationModalOpen } from '@/pages/translation/state';
import { useAnalytics } from '@/services/analytics';
import { useFlag } from '@/services/featureFlags';
import theme from '@/theme';
import styled from '@emotion/styled';
import { useState } from 'react';
import { useSetRecoilState } from 'recoil';

const Image = styled.div`
    display: flex;
    height: 100%;
    align-self: end;
    margin-left: auto;
`;

const Banner = styled.div`
    background-color: ${theme.colors.black[100]};
    border-radius: 12px;
    width: 100%;
    flex-shrink: 0;
    box-shadow:
        1.1px 2.7px 3.8px -1.2px rgba(187, 187, 187, 0.26),
        0.5px 1.3px 1.8px -0.6px rgba(187, 187, 187, 0.18),
        0.3px 0.8px 1.1px 0px rgba(187, 187, 187, 0.11);
`;

const Info = styled.div`
    padding: 32px 0;
    margin-left: 24px;
`;

const Title = styled.div`
    font-weight: 700;
    letter-spacing: -0.56px;
    color: ${theme.colors.secondary[900]};
    font-size: 28px;
    margin-bottom: 24px;
`;

const ElementsContainer = styled.div`
    display: flex;
    height: 100%;
`;

const Video = styled.div<{ bg: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    background-image: url(${props => props.bg});
    background-size: cover;
    background-position: center;
    height: 146px;
    aspect-ratio: 16/9;
    border-radius: 4px;
    margin: 12px;
    cursor: pointer;
`;

const IconCircle = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 50%;
    width: 48px;
    height: 48px;
    background: rgba(0, 0, 0, 0.55);
`;

export const TranslationBanner = (props: { show: boolean }) => {
    const setCreateModalOpen = useSetRecoilState(createTranslationModalOpen);
    const mixpanel = useAnalytics();
    const translationsMarketingVideoEnabledFlag = useFlag('translations_marketing_video_enabled', false);
    const [modalOpen, setModalOpen] = useState(false);
    const { outOfFreeVideos } = useFreeVideos();
    const setUpgradeFreePlanModalOpen = useSetRecoilState(upgradeFreePlanModalOpen);

    const handleClick = () => {
        mixpanel?.track('translate-video', {
            event: 'view',
            from: 'translate-page',
        });
        if (outOfFreeVideos) {
            setUpgradeFreePlanModalOpen(true);
            return;
        }
        setCreateModalOpen(true);
    };

    if (!props.show) {
        return null;
    }

    const videoSrc = encodeURI(`${persistentClipsDns}${'/D_ID_video_translate.mp4'}`);
    const videoThumb = encodeURI(`${persistentClipsDns}${'/D_ID_video_translate.png'}`);

    return (
        <>
            <Banner>
                <ElementsContainer>
                    {translationsMarketingVideoEnabledFlag && (
                        <Video bg={videoThumb} onClick={() => setModalOpen(true)}>
                            <IconCircle>
                                <MaskedIcon src={PlayPreview} size="24px" color="white" />
                            </IconCircle>
                        </Video>
                    )}

                    <Info>
                        <Title>Translate your video into multiple languages</Title>
                        <Button aria-valuetext="Upload your first video" color="secondary" onClick={handleClick}>
                            Upload your first video
                        </Button>
                    </Info>
                    <Image>
                        <img src={flags} alt="Banner Image" />
                    </Image>
                </ElementsContainer>
            </Banner>
            <SimpleVideoPlayerModal
                thumbnail={videoThumb}
                src={videoSrc}
                isOpen={modalOpen}
                handleClose={() => setModalOpen(false)}
                title="Translate your video into multiple languages"
            />
        </>
    );
};
